import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Star } from "lucide-react";
import { Separator } from "@/components/ui/separator";

interface SatisfactionSurveyDetailsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: any;
}

export function SatisfactionSurveyDetails({
  open,
  onOpenChange,
  data,
}: SatisfactionSurveyDetailsProps) {
  if (!data) return null;

  const StarRating = ({ value, max = 5 }: { value: number; max?: number }) => {
    return (
      <div className="flex gap-1">
        {Array.from({ length: max }).map((_, i) => (
          <Star
            key={i}
            className={`h-5 w-5 ${
              i < value
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-200 text-gray-200'
            }`}
          />
        ))}
        <span className="ml-2 text-sm font-medium">{value}/{max}</span>
      </div>
    );
  };

  const getRatingLabel = (value: number) => {
    switch (value) {
      case 1:
        return 'Muito insatisfeito';
      case 2:
        return 'Insatisfeito';
      case 3:
        return 'Neutro';
      case 4:
        return 'Satisfeito';
      case 5:
        return 'Muito satisfeito';
      default:
        return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Pesquisa de Satisfação</DialogTitle>
          <DialogDescription>
            Respostas completas da pesquisa de satisfação
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          <div className="space-y-2">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase">
              Nota Geral
            </h4>
            <div className="pl-4">
              <StarRating value={data.overallRating || 0} />
              <p className="text-sm text-muted-foreground mt-1">
                {getRatingLabel(data.overallRating || 0)}
              </p>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase">
              Qualidade do Atendimento
            </h4>
            <div className="pl-4">
              <StarRating value={data.serviceQuality || 0} />
              <p className="text-sm text-muted-foreground mt-1">
                {getRatingLabel(data.serviceQuality || 0)}
              </p>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase">
              Comunicação do Profissional
            </h4>
            <div className="pl-4">
              <StarRating value={data.communication || 0} />
              <p className="text-sm text-muted-foreground mt-1">
                {getRatingLabel(data.communication || 0)}
              </p>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase">
              Recomendação
            </h4>
            <div className="pl-4">
              <div className="flex items-center gap-2">
                {data.wouldRecommend ? (
                  <>
                    <span className="text-green-600 font-semibold">✓ Sim, recomendaria</span>
                  </>
                ) : (
                  <>
                    <span className="text-red-600 font-semibold">✗ Não recomendaria</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {data.comments && (
            <>
              <Separator />
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase">
                  Comentários Adicionais
                </h4>
                <div className="pl-4">
                  <p className="text-sm whitespace-pre-wrap">{data.comments}</p>
                </div>
              </div>
            </>
          )}

          {/* Resumo visual */}
          <Separator />
          <div className="bg-muted p-4 rounded-lg space-y-2">
            <h4 className="font-semibold text-sm">Resumo</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Média Geral</div>
                <div className="text-2xl font-bold">
                  {(((data.overallRating || 0) + (data.serviceQuality || 0) + (data.communication || 0)) / 3).toFixed(1)}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Recomendação</div>
                <div className="text-2xl font-bold">
                  {data.wouldRecommend ? '100%' : '0%'}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Comentários</div>
                <div className="text-2xl font-bold">
                  {data.comments ? 'Sim' : 'Não'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

